import { useId } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import { getColorClass } from 'src/modules/shared/components/atoms/text/text.styles';
import type { LabelListProps } from 'src/modules/shared/types/label-list.type';
import { classes } from 'src/modules/shared/utils/styles.util';

/**
 * Presents complete read-only labels using the compact menu's content spacing.
 * @param props - Caption, ordered labels, and optional container classes.
 * @returns A captioned semantic list with decorative markers and wrapping text.
 */
export const LabelList = ({ caption, items, className }: LabelListProps) => {
  const captionId = useId();

  return (
    <div className={classes('min-w-0 p-[5px]', className)}>
      <div id={captionId}>
        <Text
          as="div"
          size="tiny"
          disableAutoLink
          className="px-2 pb-[5px] pt-[3px] leading-[1.5] text-slate-500"
        >
          {caption}
        </Text>
      </div>
      <ul aria-labelledby={captionId} className="m-0 list-none p-0">
        {items.map(({ key, label, icon, variant }) => (
          <li
            key={key}
            className={classes(
              'flex min-w-0 items-start gap-2 p-2',
              variant ? getColorClass(variant) : 'text-slate-900'
            )}
          >
            <span
              aria-hidden="true"
              className={classes(
                'flex h-[19.5px] w-4 shrink-0 items-center justify-center',
                !variant && 'text-slate-400'
              )}
            >
              {icon ?? <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
            <Text
              as="span"
              size="small"
              disableAutoLink
              className="min-w-0 whitespace-normal break-words text-left !text-[13px] leading-[1.5]"
            >
              {label}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
};
